import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Save } from "lucide-react";
import { API, errMsg } from "@/lib/api";

export const WELCOME = "¡Hola! Gracias por escribir a En Dos Mundos. Cuéntanos qué taller te interesa y te enviaremos las instrucciones de pago por WhatsApp.";

const PRODUCTS = {
  "equipo-calma": { name: "Pack Equipo en Calma", price: 39 },
  "rabietas-2-3": { name: "Rabietas: acompañar con límites", price: 19 },
};

const copy = (t, msg = "Mensaje copiado") => { navigator.clipboard?.writeText(t); toast.success(msg); };
const field = "w-full rounded-xl border-2 border-[#d9cbb8] focus:border-[var(--ink)] outline-none px-3 py-2 bg-[var(--cream)]";

const Card = ({ n, title, testid, children }) => (
  <div data-testid={testid} className="rounded-2xl bg-white p-5 sm:p-6">
    <p className="font-display text-xl"><span className="text-[var(--accent)]">{n}.</span> {title}</p>
    <div className="mt-3">{children}</div>
  </div>
);

export const payMsg = (name, s, productId = "equipo-calma") => {
  const product = PRODUCTS[productId] || PRODUCTS["equipo-calma"];
  const cardLink = productId === "rabietas-2-3" ? s.card_link_rabietas : s.card_link;
  const lines = [`¡Hola${name ? ` ${name}` : ""}! ${product.name} cuesta ${product.price} €. Puedes pagar como prefieras:`];
  if (cardLink) lines.push(`💳 Con tarjeta: ${cardLink}`);
  if (s.bizum_number) lines.push(`📲 Por Bizum al ${s.bizum_number} (concepto: ${product.name} + tu nombre)`);
  lines.push("En cuanto confirmemos el pago, te enviamos tu código de acceso.");
  return lines.join("\n");
};

export const SalesMessages = ({ token }) => {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [s, setS] = useState({ card_link: "", card_link_rabietas: "", bizum_number: "" });
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("equipo-calma");
  useEffect(() => { axios.get(`${API}/admin/settings`, auth).then((r) => setS(r.data)).catch(() => {}); }, [auth]);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await axios.put(`${API}/admin/settings`, s, auth); setS(data); toast.success("Datos de pago guardados"); } catch (err) { toast.error(errMsg(err)); }
  };
  const msg = payMsg(name.trim(), s, productId);
  const activeCardLink = productId === "rabietas-2-3" ? s.card_link_rabietas : s.card_link;
  const ready = activeCardLink || s.bizum_number;
  const cardLinkField = productId === "rabietas-2-3" ? "card_link_rabietas" : "card_link";

  return (
    <section data-testid="sales-messages" className="mt-8 rounded-2xl bg-[var(--cream-2)] p-4 sm:p-6 space-y-4">
      <div>
        <p className="font-display text-2xl">Mensajes de venta</p>
        <p className="text-sm text-[#5a4f46]">Así atiendes cada compra por WhatsApp, en 3 pasos.</p>
      </div>

      <Card n={1} title="Bienvenida automática" testid="welcome-card">
        <p className="rounded-xl bg-[#DCF3D4] p-4 text-sm" data-testid="welcome-text">{WELCOME}</p>
        <button data-testid="copy-welcome-button" onClick={() => copy(WELCOME)} className="btn-ghost mt-3 !py-2"><Copy size={16} /> Copiar</button>
        <ol className="mt-3 text-sm text-[#5a4f46] list-decimal pl-5 space-y-1">
          <li>Abre WhatsApp Business → Ajustes → Herramientas para la empresa → Mensaje de bienvenida.</li>
          <li>Actívalo y pega este texto. Se envía solo a quien te escribe por primera vez (o tras 14 días sin hablar).</li>
        </ol>
      </Card>

      <Card n={2} title="Mensaje de pago" testid="payment-card">
        <label className="block text-sm font-semibold mb-2" htmlFor="payment-product">Taller</label>
        <select id="payment-product" data-testid="payment-product-select" value={productId} onChange={(e) => setProductId(e.target.value)} className={`${field} mb-3`}>
          <option value="equipo-calma">Equipo en Calma · 39 €</option>
          <option value="rabietas-2-3">Rabietas: acompañar con límites · 19 €</option>
        </select>
        <form onSubmit={save} className="grid sm:grid-cols-[1fr_200px_auto] gap-2">
          <input data-testid="card-link-input" value={activeCardLink || ""} onChange={(e) => setS({ ...s, [cardLinkField]: e.target.value })} placeholder={`Enlace de tarjeta para ${PRODUCTS[productId].name}`} className={field} />
          <input data-testid="bizum-number-input" value={s.bizum_number} onChange={(e) => setS({ ...s, bizum_number: e.target.value })} placeholder="Tu número de Bizum" className={field} />
          <button data-testid="save-payment-settings-button" className="btn-accent !py-2"><Save size={16} /> Guardar</button>
        </form>
        <input data-testid="payment-buyer-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de la persona (opcional)" className={`${field} mt-3`} />
        <p className="mt-3 rounded-xl bg-[#DCF3D4] p-4 text-sm whitespace-pre-line" data-testid="payment-message-text">{msg}</p>
        {!ready && <p className="text-xs text-[#c0392b] mt-2" data-testid="payment-missing-note">Guarda tu enlace de tarjeta o tu Bizum para que aparezcan en el mensaje.</p>}
        <button data-testid="copy-payment-message-button" onClick={() => copy(msg)} className="btn-ghost mt-3 !py-2"><Copy size={16} /> Copiar mensaje de pago</button>
        <details className="mt-4 text-sm text-[#5a4f46]" data-testid="stripe-guide">
          <summary className="cursor-pointer font-semibold text-[var(--ink)]">Cómo crear tu enlace de pago con tarjeta (Stripe)</summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            <li>Entra en dashboard.stripe.com con tu cuenta (o créala gratis).</li>
            <li>Ve a Enlaces de pago → Nuevo.</li>
            <li>Crea el producto “{PRODUCTS[productId].name}” por {PRODUCTS[productId].price} € (EUR), en un único pago.</li>
            <li>Pulsa Crear enlace, cópialo y pégalo arriba en "Enlace de pago con tarjeta".</li>
          </ol>
        </details>
      </Card>

      <Card n={3} title="Mensaje con el código" testid="code-message-card">
        <p className="text-sm text-[#5a4f46]">Cuando te confirme el pago, crea su código arriba y pulsa "Enviar por WhatsApp" o el icono de copiar de su fila: el mensaje ya va preparado.</p>
      </Card>
    </section>
  );
};
